(ns ui.gallery.move
  "Optimistic updates of the gallery query result after a move (drag &
   drop or the 'Move' popover), as pure functions over the result of
   the `:query/gallery` (see `ui.gallery.query/result`).

   The `:query/gallery` scans all story medias of the organization and
   takes seconds for large galleries, so waiting for the refresh after
   a move leaves the moved entries or folder in the old place for that
   long. Instead the loaded result is updated right away and the
   refresh corrects it later (e.g. the entry count when the moved
   entries were not all loaded).")

(defn loaded-entries
  "The loaded entries of the `result` in display order."
  [result]
  (map val
       (sort-by key
                (:gallery/entries result))))

(defn index-entries
  "Numbers the `entries` (in display order) from the `offset` on, the
   positions the grid renders."
  [offset entries]
  (into {}
        (map-indexed (fn [idx entry]
                       [(+ offset idx) entry]))
        entries))

(defn remove-entries
  "Removes the entries with the `eids` from the loaded window of the
   `result` and closes the gaps: the entries after them move up. The
   entry count shrinks by the number of removed loaded entries only,
   since the eids may include entries outside the loaded window."
  [result eids]
  (let [eids (set eids)
        loaded (loaded-entries result)
        remaining (remove (comp eids :story-media/eid)
                          loaded)
        removed-count (- (count loaded)
                         (count remaining))]
    (-> result
        (assoc :gallery/entries
               (index-entries (:gallery/offset result 0)
                              remaining))
        (update :gallery/entry-count
                (fnil - 0)
                removed-count))))

(defn add-entries
  "Adds the `entries` (moved into the shown folder) to the loaded
   window of the `result` in display order (newest eid first) and
   grows the entry count by the number of added entries. Entries that
   are loaded already stay as they are."
  [result entries]
  (let [loaded (loaded-entries result)
        loaded-eids (set (map :story-media/eid loaded))
        new-entries (remove (comp loaded-eids :story-media/eid)
                            entries)]
    (-> result
        (assoc :gallery/entries
               (index-entries (:gallery/offset result 0)
                              (sort-by (comp - :story-media/eid)
                                       (concat loaded new-entries))))
        (update :gallery/entry-count
                (fnil + 0)
                (count new-entries)))))

(defn remove-folder
  "Removes the subfolder `folder-uuid` from the shown folder of the
   folder tree."
  [result folder-uuid]
  (if-let [current-folder (get-in result [:folder-tree :current-folder])]
    (update-in result
               [:folder-tree :folders current-folder :children]
               (fnil disj #{})
               folder-uuid)
    result))

(defn add-folder
  "Adds the `folder` (a map with the `:name`) with the `folder-uuid` as
   subfolder to the shown folder of the folder tree."
  [result folder-uuid folder]
  (if-let [current-folder (get-in result [:folder-tree :current-folder])]
    (-> result
        (update-in [:folder-tree :folders current-folder :children]
                   (fnil conj #{})
                   folder-uuid)
        (update-in [:folder-tree :folders folder-uuid]
                   merge
                   folder))
    result))
